import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { CropImageDialog } from "@/components/image-crop/CropImageDialog";
import type { CropRect, Size } from "@/components/image-crop/cropGeometry";
import {
  decodeImage,
  exportCroppedImage,
} from "@/components/image-crop/exportCroppedImage";
import { useGetCurrentLocalFacilityId } from "modules/facility/facilityHooks";
import type { ClubhouseAdKitImageDto } from "entities/responses/adkitImage";
import { useUploadAdKitImagesMutation } from "../../api/endpoints/images";
import { imageDisplayName } from "./imageLibraryViewModel";
import {
  cropDescription,
  cropExportTarget,
  croppedImageName,
  type CropPlacement,
} from "./placementCrop";

export type CropSource =
  | { kind: "file"; file: File; name: string }
  | { kind: "library"; image: ClubhouseAdKitImageDto };

interface PlacementCropDialogProps {
  source: CropSource | null;
  placement: CropPlacement | null;
  onCancel: () => void;
  /** The new library image; the source image is left untouched. */
  onCropped: (image: ClubhouseAdKitImageDto) => void;
}

interface Loaded {
  bitmap: ImageBitmap;
  url: string;
  size: Size;
  type: string;
}

async function loadSource(source: CropSource): Promise<Blob> {
  if (source.kind === "file") return source.file;
  if (!source.image.imageUrl) throw new Error("This image has no file.");
  // no-store: a copy cached before CORS was allowed would still be unreadable.
  const response = await fetch(source.image.imageUrl, { cache: "no-store" });
  if (!response.ok) throw new Error("We couldn't open that image.");
  return response.blob();
}

function sourceName(source: CropSource): string {
  return source.kind === "file" ? source.name : imageDisplayName(source.image);
}

/** Crops an image to one placement's exact shape and size, then saves it as a new library image. */
export function PlacementCropDialog({
  source,
  placement,
  onCancel,
  onCropped,
}: PlacementCropDialogProps) {
  const facilityId = useGetCurrentLocalFacilityId();
  const [uploadImages] = useUploadAdKitImagesMutation();
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!source) return;
    let cancelled = false;
    let url: string | null = null;
    setLoaded(null);
    setLoadError(null);
    loadSource(source)
      .then(async (blob) => {
        const bitmap = await decodeImage(blob);
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setLoaded({
          bitmap,
          url,
          size: { width: bitmap.width, height: bitmap.height },
          type: blob.type,
        });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(
            err instanceof Error ? err.message : "We couldn't open that image.",
          );
        }
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [source]);

  const open = source !== null && placement !== null;
  const name = source ? sourceName(source) : "";

  const save = async (rect: CropRect) => {
    if (!loaded || !placement) return;
    setIsSaving(true);
    try {
      const blob = await exportCroppedImage(
        loaded.bitmap,
        rect,
        cropExportTarget(placement, loaded.type),
      );
      const response = await uploadImages({
        facilityId,
        // The crop is the placement's exact size, so size warnings don't apply.
        files: [
          {
            file: blob,
            imageName: croppedImageName(name, placement.aspectRatio),
            overrideWarnings: true,
          },
        ],
      }).unwrap();
      const created = response.uploadedImages?.[0];
      if (!created) throw new Error("upload returned no image");
      toast.success("Cropped copy saved to the library.");
      onCropped(created);
    } catch {
      toast.error("Couldn't save the cropped image. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CropImageDialog
      open={open}
      imageUrl={loaded?.url ?? null}
      naturalSize={loaded?.size ?? null}
      aspectRatio={placement?.aspectRatio ?? 2}
      title={`Crop for ${placement?.name ?? "a placement"}`}
      description={
        loadError ??
        (loaded && placement
          ? cropDescription(name, placement, loaded.type)
          : "Loading image…")
      }
      saveLabel={isSaving ? "Saving…" : "Save cropped copy"}
      isSaving={isSaving}
      onCancel={onCancel}
      onSave={(rect) => void save(rect)}
    />
  );
}
